import { UserRequest } from 'Helpers/request-by-user';
import { DEFERRED_OPTION, ProviderSelection } from 'constants/providers';

export const ENROLL_PROGRESS_STORAGE_PREFIX = 'enrollProgress';

export const PROVIDER_STEP = 20;
export const SUBDOMAIN_STEP = 16;
const HIDDEN_NAV_STEPS = new Set([0, 10, 13, SUBDOMAIN_STEP, PROVIDER_STEP]);

export interface StoredEnrollmentProgress {
  step: number;
  type: string;
}

export interface ResolvedEnrollmentProgress {
  step: number;
  enrollmentType: string;
  showNav: boolean;
}

/**
 * Propósito: Clave de localStorage del progreso, por usuario.
 * Evita que dos cuentas en el mismo browser hereden el paso.
 */
const progressStorageKey = (userId: string | number): string => (
  `${ENROLL_PROGRESS_STORAGE_PREFIX}:${userId}`
);

/**
 * Propósito: Los pasos 0, 10, 13, 16 y 20 tienen su propia CTA.
 */
export const shouldShowNavForStep = (step: number): boolean => (
  !HIDDEN_NAV_STEPS.has(step)
);

/**
 * Propósito: Guarda el paso y el tipo de enrollment para sobrevivir un refresh.
 */
export const persistEnrollmentProgress = (
  userId: string | number | null | undefined,
  progress: StoredEnrollmentProgress,
): void => {
  if (userId == null || userId === '' || userId === 'undefined') {
    /**
     * Output:
     * Side effects — Sin userId no se escribe nada
     */
    return;
  }

  try {
    localStorage.setItem(progressStorageKey(userId), JSON.stringify(progress));
    /**
     * Output:
     * Side effects — Escribe `{ step, type }` en localStorage para este userId
     */
  } catch (_error) {
    // Storage bloqueado: el wizard sigue, solo no reanuda al recargar.
  }
};

/**
 * Propósito: Lee el progreso guardado de este usuario.
 */
export const readEnrollmentProgress = (
  userId: string | number | null | undefined,
): StoredEnrollmentProgress | null => {
  if (userId == null || userId === '' || userId === 'undefined') {
    /**
     * Output:
     * @returns null — Sin userId no hay progreso que leer
     */
    return null;
  }

  try {
    const raw = localStorage.getItem(progressStorageKey(userId));
    if (!raw) {
      /**
       * Output:
       * @returns null — Este usuario no tiene progreso guardado
       */
      return null;
    }

    const parsed = JSON.parse(raw) as StoredEnrollmentProgress;
    const step = Number(parsed?.step);
    if (!Number.isInteger(step) || step < 0) {
      /**
       * Output:
       * @returns null — Payload corrupto; se ignora
       */
      return null;
    }

    /**
     * Output:
     * @returns StoredEnrollmentProgress — Paso y tipo persistidos
     */
    return {
      step,
      type: typeof parsed.type === 'string' ? parsed.type : '',
    };
  } catch (_error) {
    /**
     * Output:
     * @returns null — JSON inválido o storage bloqueado
     */
    return null;
  }
};

/**
 * Propósito: Borra el progreso al cerrar sesión para no heredarlo.
 */
export const clearEnrollmentProgress = (
  userId: string | number | null | undefined,
): void => {
  if (userId == null || userId === '' || userId === 'undefined') return;

  try {
    localStorage.removeItem(progressStorageKey(userId));
    /**
     * Output:
     * Side effects — Elimina el progreso de este userId
     */
  } catch (_error) {
    // Sin storage no hay nada que limpiar.
  }
};

/**
 * Propósito: ¿El request ya trae un borrador de KYC reanudable?
 */
const hasDraftKyc = (request: UserRequest): boolean => {
  const dwolla = request.info_dwolla;
  if (!dwolla || typeof dwolla !== 'object') {
    /**
     * Output:
     * @returns false — Sin info_dwolla no hay formulario que pintar
     */
    return false;
  }

  /**
   * Output:
   * @returns boolean — true si hay nombre, negocio o email para hidratar el form
   */
  return Boolean(dwolla.firstName || dwolla.businessName || dwolla.email);
};

/**
 * Propósito: Decide el paso del wizard al recargar.
 * El backend manda si ya se envió o hay que crear subdominio; si no, se usa
 * el paso guardado para no volver al inicio a mitad del formulario.
 */
export const resolveEnrollmentProgress = ({
  request,
  stored,
}: {
  request: UserRequest | null;
  stored: StoredEnrollmentProgress | null;
}): ResolvedEnrollmentProgress => {
  const storedStep = stored?.step ?? null;
  const storedType = stored?.type === 'business' ? 'business' : '';

  if (request) {
    const dwollaStatus = Number(request.status_dwolla_id) || 0;
    const usioStatus = Number(request.status_usio_id) || 0;

    if (dwollaStatus === 2 && usioStatus === 4) {
      /**
       * Output:
       * @returns paso 5 — Cuenta lista, mensaje de confirmación
       */
      return { step: 5, enrollmentType: 'business', showNav: false };
    }

    if (dwollaStatus === 2) {
      /**
       * Output:
       * @returns paso 4 — Click-to-agree de Usio
       */
      return { step: 4, enrollmentType: 'business', showNav: false };
    }

    if (dwollaStatus === 3) {
      /**
       * Output:
       * @returns paso 3 — Solicitud rechazada
       */
      return { step: 3, enrollmentType: 'business', showNav: true };
    }

    if (
      dwollaStatus === 9
      || dwollaStatus === 10
      || dwollaStatus === 12
      || dwollaStatus === 13
    ) {
      /**
       * Output:
       * @returns paso Plaid/post-día según status_dwolla
       */
      return {
        step: dwollaStatus,
        enrollmentType: 'business',
        showNav: shouldShowNavForStep(dwollaStatus),
      };
    }

    const isSubmitted = dwollaStatus === 1 || usioStatus === 1;
    if (
      isSubmitted
      || request.deferred === true
      || dwollaStatus === 16
      || request.finished === true
    ) {
      /**
       * Output:
       * @returns paso 16 — Crear subdominio (enviada, diferida o ya finalizada)
       */
      return { step: SUBDOMAIN_STEP, enrollmentType: 'business', showNav: false };
    }

    const inProgressStep = storedStep === 2 && !hasDraftKyc(request)
      ? 1
      : storedStep;

    if (inProgressStep === 1 || inProgressStep === 2 || inProgressStep === PROVIDER_STEP) {
      /**
       * Output:
       * @returns paso guardado — Borrador a mitad del KYC o elección de proveedor
       */
      return {
        step: inProgressStep,
        enrollmentType: 'business',
        showNav: shouldShowNavForStep(inProgressStep),
      };
    }

    if (hasDraftKyc(request)) {
      /**
       * Output:
       * @returns paso 1 — Hay KYC guardado pero no hay paso en localStorage
       */
      return { step: 1, enrollmentType: 'business', showNav: true };
    }

    if (storedStep === SUBDOMAIN_STEP) {
      /**
       * Output:
       * @returns paso 16 — Estaba creando el subdominio y el request ya existe
       */
      return { step: SUBDOMAIN_STEP, enrollmentType: 'business', showNav: false };
    }
  }

  if (storedStep === 1 || storedStep === PROVIDER_STEP) {
    /**
     * Output:
     * @returns paso 1 o 20 — Progreso local sin request todavía
     */
    return {
      step: storedStep,
      enrollmentType: storedStep === 1 ? 'business' : storedType,
      showNav: shouldShowNavForStep(storedStep),
    };
  }

  /**
   * Output:
   * @returns paso 0 — Postulación nueva o sin progreso reanudable
   */
  return { step: 0, enrollmentType: storedType, showNav: false };
};

export interface PaymentSetupProgress extends ResolvedEnrollmentProgress {
  providerChoice: ProviderSelection | null;
}

/**
 * Propósito: Elige un proveedor habilitado para `/payment-setup`.
 * Descarta Dwolla (u otro) si el admin lo apagó, y si solo queda uno lo
 * selecciona para no pintar cards de elección.
 */
export const resolveEnabledPaymentChoice = (
  currentChoice: ProviderSelection | null | undefined,
  enabledCodes: string[],
): ProviderSelection | null => {
  if (
    currentChoice
    && currentChoice !== DEFERRED_OPTION
    && enabledCodes.includes(currentChoice)
  ) {
    /**
     * Output:
     * @returns ProviderSelection — La elección guardada sigue disponible
     */
    return currentChoice;
  }

  if (enabledCodes.length === 1) {
    /**
     * Output:
     * @returns ProviderSelection — Único proveedor activo (p. ej. solo Usio)
     */
    return enabledCodes[0] as ProviderSelection;
  }

  /**
   * Output:
   * @returns null — Hay que pedir una elección entre los habilitados
   */
  return null;
};

/**
 * Propósito: Paso del wizard cuando el abogado vuelve desde el portal a
 * completar el KYC. No manda a crear subdominio: el portal ya existe.
 */
export const resolvePaymentSetupProgress = ({
  stored,
  enabledCodes,
  currentChoice,
}: {
  stored: StoredEnrollmentProgress | null;
  enabledCodes: string[];
  currentChoice: ProviderSelection | null;
}): PaymentSetupProgress => {
  const usableChoice = resolveEnabledPaymentChoice(currentChoice, enabledCodes);
  const storedStep = stored?.step;

  if (storedStep === 1 || storedStep === 2) {
    /**
     * Output:
     * @returns PaymentSetupProgress — Reanuda el KYC a mitad de completar
     */
    return {
      step: storedStep,
      enrollmentType: 'business',
      showNav: shouldShowNavForStep(storedStep),
      providerChoice: usableChoice,
    };
  }

  if (usableChoice) {
    /**
     * Output:
     * @returns PaymentSetupProgress — Formulario de KYC; sin cards si solo hay un proveedor
     */
    return {
      step: 1,
      enrollmentType: 'business',
      showNav: true,
      providerChoice: usableChoice,
    };
  }

  /**
   * Output:
   * @returns PaymentSetupProgress — Paso de proveedor, solo con los habilitados
   */
  return {
    step: PROVIDER_STEP,
    enrollmentType: 'business',
    showNav: false,
    providerChoice: null,
  };
};
